// Teeth-photo set defaults. Mirrors the backend source of truth (spec: chatbot teeth photos, "v2 - Practice-chosen photo sets").
// Used for defaults, resets, validation and the live preview only - the backend validates and the widget renders.
export const TEETH_PHOTOS_DEFAULT_INTRO =
  "To help us assess your smile and bite, you can send us a few quick photos of your teeth. One of our Treatment Coordinators will review them for an initial assessment. It's optional.";
export const DEFAULT_TIPS =
  "Use daylight if you can, keep the phone steady, and avoid shadows. A friend can help — or use your selfie camera and we'll guide you.";
export const DEFAULT_PRIVACY_NOTE = "Only your mouth and teeth are needed — you don't have to show your face.";

export const CHOICES_VERSION = 1;
export const MAX_SHOTS = 6;
export const MAX_CUSTOM_SHOTS = 3;
export const LIMITS = { intro: 500, title: 60, instructions: 300, tips: 400, privacyNote: 300 };

export const LIBRARY_SHOTS = [
  { key: 'front_bite', title: 'Front, teeth together', instructions: 'Face the camera. Bite down so your back teeth touch, and part your lips so we can see your teeth.' },
  { key: 'left_bite', title: 'Left side, teeth together', instructions: 'Turn your head to your left. Keep biting together so we can see how your teeth fit.' },
  { key: 'right_bite', title: 'Right side, teeth together', instructions: 'Turn your head to your right. Keep biting together.' },
  { key: 'open_wide', title: 'Front, mouth open', instructions: 'Face the camera and open your mouth as wide as is comfortable, so we can see your top and bottom teeth.' },
  { key: 'smile_front', title: 'Natural smile, straight on', instructions: 'Face the camera and give us a natural smile with your teeth showing.' },
  { key: 'smile_left', title: 'Natural smile, left side', instructions: 'Turn your head to your left and keep smiling.' },
  { key: 'smile_right', title: 'Natural smile, right side', instructions: 'Turn your head to your right and keep smiling.' },
];
export const DEFAULT_SHOT_KEYS = ['front_bite', 'left_bite', 'right_bite', 'open_wide'];
export const CUSTOM_KEYS = ['custom_1', 'custom_2', 'custom_3'];

export const CROP_OPTIONS = [
  { value: 'mouth_only', label: 'Mouth only', help: 'Every photo is cropped to the mouth and teeth automatically.' },
  { value: 'visitor_choice', label: 'Visitor chooses', help: 'Visitors are asked once whether to keep only their mouth (recommended).' },
  { value: 'full', label: 'Keep full photo', help: 'Photos are kept exactly as taken, with no cropping.' },
];
export const DEFAULT_CROP = 'visitor_choice';

export const libraryShot = (key) => LIBRARY_SHOTS.find((s) => s.key === key) || null;
export const isLibraryKey = (key) => !!libraryShot(key);
export const isCustomKey = (key) => CUSTOM_KEYS.includes(key);
/** Default title for a library key, or null. */
export const libraryTitle = (key) => libraryShot(key)?.title ?? null;

const defaultShots = () => DEFAULT_SHOT_KEYS.map((k) => {
  const { key, title, instructions } = libraryShot(k);
  return { key, title, instructions };
});

export const defaultConfig = () => ({
  shots: defaultShots(),
  tips: DEFAULT_TIPS,
  privacyNote: DEFAULT_PRIVACY_NOTE,
  crop: DEFAULT_CROP,
});

const asText = (v) => (typeof v === 'string' ? v : '');

/** choices (object, JSON string, null...) -> editor config. Missing/empty/unusable choices => defaults. */
export function parseChoices(choices) {
  let raw = choices;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  const base = defaultConfig();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const shots = Array.isArray(raw.shots)
    ? raw.shots
      .filter((s) => s && typeof s === 'object' && (isLibraryKey(s.key) || isCustomKey(s.key)))
      .map((s) => ({ key: s.key, title: asText(s.title), instructions: asText(s.instructions) }))
    : [];
  if (!shots.length) return base;
  return {
    shots,
    tips: typeof raw.tips === 'string' ? raw.tips : base.tips,
    privacyNote: typeof raw.privacy_note === 'string' ? raw.privacy_note : base.privacyNote,
    crop: CROP_OPTIONS.some((o) => o.value === raw.crop) ? raw.crop : DEFAULT_CROP,
  };
}

/** editor config -> the `choices` JSON the API stores (version 1). */
export const buildChoices = (config) => ({
  version: CHOICES_VERSION,
  shots: config.shots.map((s) => ({ key: s.key, title: s.title.trim(), instructions: s.instructions.trim() })),
  tips: config.tips.trim(),
  privacy_note: config.privacyNote.trim(),
  crop: config.crop,
});

/** Client-side mirror of the server rules. Returns { shots: {key: {title?, instructions?}}, count?, intro?, tips?, privacyNote? }. */
export function validateConfig(config, intro = '') {
  const errors = { shots: {} };
  if (intro.length > LIMITS.intro) errors.intro = `Message must be ${LIMITS.intro} characters or fewer`;
  if (config.shots.length < 1) errors.count = 'Choose at least one photo';
  if (config.shots.length > MAX_SHOTS) errors.count = `Choose at most ${MAX_SHOTS} photos`;
  config.shots.forEach((s) => {
    const e = {};
    const title = s.title.trim();
    const instructions = s.instructions.trim();
    if (!title) e.title = 'Title is required';
    else if (title.length > LIMITS.title) e.title = `Title must be ${LIMITS.title} characters or fewer`;
    if (!instructions) e.instructions = 'Instructions are required';
    else if (instructions.length > LIMITS.instructions) e.instructions = `Instructions must be ${LIMITS.instructions} characters or fewer`;
    if (e.title || e.instructions) errors.shots[s.key] = e;
  });
  if (config.tips.trim().length > LIMITS.tips) errors.tips = `Tips must be ${LIMITS.tips} characters or fewer`;
  if (config.privacyNote.trim().length > LIMITS.privacyNote) errors.privacyNote = `Privacy note must be ${LIMITS.privacyNote} characters or fewer`;
  return errors;
}

export const hasErrors = (errors) =>
  !!(errors.count || errors.intro || errors.tips || errors.privacyNote || Object.keys(errors.shots || {}).length);

/** First unused custom key, or null when all three are taken. */
export const nextCustomKey = (shots) => CUSTOM_KEYS.find((k) => !shots.some((s) => s.key === k)) || null;
